import { gql, useQuery } from "@apollo/client";
import useCheckPC from "@repo/hooks/utils/useCheckPC";
import type { IRankingObjectTypeQuery } from "@repo/shopify/src/queries/others-gql";
import { rankingMetaobjectQuery } from "@repo/shopify/src/queries/others-gql";
import { getTotalProductCount } from "@repo/shopify/src/utils";
import { checkMembershipAccessible } from "@repo/shopify/src/utils/membership";
import type { Collection, Product } from "@shopify/hydrogen-react/storefront-api-types";
import classNames from "classnames";
import qs from "query-string";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import SimpleBar from "simplebar-react";
import "simplebar-react/dist/simplebar.min.css";

import Button from "@components/atoms/Button";
import LoadingIcon from "@components/atoms/LoadingIcon";
import PageTitle from "@components/atoms/PageTitle";
import Select from "@components/atoms/Select";
import Loading from "@components/molecules/Loading";
import Footer from "@components/organisms/Footer";

import ProductCard from "@molecules/Card/ProductCard";

import Filter from "@organisms/Filter";
import ProductGrid from "@organisms/ProductGrid";

import useLayout from "@hooks/reducerHooks/useLayout";
import useMeta from "@hooks/reducerHooks/useMeta";
import useUser from "@hooks/reducerHooks/useUser";
import useCollections from "@hooks/useCollections";

import { COLLECTION_SORT } from "@const/constants";
import { URLS } from "@const/urls";

import { getCollectionsAllUrl } from "@utils/archive-sale";
import { NOTIFICATIONS } from "@utils/notifications";
import { getProductLaunch } from "@utils/product";

export default function Collections({ isSaleShutDown }: { isSaleShutDown: boolean }) {
  const navigate = useNavigate();
  const location = useLocation();

  const { isPC } = useCheckPC();
  const { setMetaTag } = useMeta();
  const { customer, accessToken } = useUser();
  const { openToast, showFooter } = useLayout();

  const loaderEl = useRef(null);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [activeFilterItem, setActiveFilterItem] = useState("brand");

  const { data, products, hasNextPage, loading } = useCollections(loaderEl);

  const { t } = useTranslation();

  const [searchParams, setSearchParams] = useSearchParams();
  const sortIndex = Number(searchParams.get("sortIndex")) || 0;

  const sortList = COLLECTION_SORT.map((item) => ({
    ...item,
    value: `${item.value},${Boolean(item.reverse)}`,
    reverse: Boolean(item.reverse),
  }));

  useEffect(() => {
    navigate(URLS.ACCOUNT_ORDER_HISTORY, { replace: true });
    setIsFilterOpen(isPC);
  }, [isPC]);

  useEffect(() => {
    showFooter(!hasNextPage);
  }, [hasNextPage]);

  useEffect(() => {
    setMetaTag({ title: `TUNE.KR - ${data?.collection.title.toUpperCase() || ""}` });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data?.collection.title]);

  const { data: rankingData, loading: rankingLoading } = useQuery<IRankingObjectTypeQuery>(
    gql(rankingMetaobjectQuery.query),
    {
      variables: { type: "ranking", first: 1 },
      notifyOnNetworkStatusChange: true,
      onError: (error) => {
        throw new Error(`[SHOPIFY][BlogHomeBanner] Failed to query : ${JSON.stringify(error)}`);
      },
    },
  );

  const familyRanking = useMemo(() => {
    const list =
      rankingData?.metaobjects.edges
        .find(({ node }) => node.handle === "family-ranking")
        ?.node.fields[0]?.references?.edges.filter(({ node }) => (node as Product).availableForSale)
        .filter(({ node }) => (node as Product).tags.includes("ARCHIVESALE"))
        ?.slice(0, 10) || [];
    return list;
  }, [rankingData]);

  const familyRankingUpdatedAt = useMemo(() => {
    if (!rankingData) return "";
    const updatedAt =
      rankingData.metaobjects.edges.find(({ node }) => node.handle === "family-ranking")?.node.updatedAt ?? "";
    const date = new Date(updatedAt);
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    const hour = date.getHours();

    return `${month}. ${day}. ${year} ${hour}:00 GMT`;
  }, [rankingData]);

  // 멤버십 유효성 확인
  if (data) {
    const isMembershipAccessible = checkMembershipAccessible(data.collection as Collection, customer);
    if (!isMembershipAccessible) {
      navigate(URLS.LOGIN);
      if (accessToken) {
        openToast(NOTIFICATIONS.WARN_NO_ACCESS);
      } else {
        openToast(NOTIFICATIONS.WARN_REQUEST_LOGIN);
        // openLoginModal({ redirectUrl: pathname });
      }
    }
  }

  // // 쿼리 전 로딩 상태일 때
  // if (!data && loading) {
  //   return <Loading />;
  // }

  // 쿼리 후 data 값이 들어왔는데도 컬렉션 상품이 0개일 때
  if (data && products.length === 0) {
    return (
      <section className="pc:col-span-12 pc:px-5 pc:text-center col-span-6 px-4">
        <PageTitle title={`We're Sorry`} />
        <p className="my-5 text-center">{`${data.collection.title} ${t("collection.ready")}`}</p>
        <Button
          className="mx-auto"
          onClick={() => {
            navigate(getCollectionsAllUrl());
          }}
        >
          Continue Shopping
        </Button>
      </section>
    );
  }

  return (
    <section className="pc:col-span-12 pc:grid-cols-12 pc:gap-5 col-span-6 grid grid-cols-6 gap-2.5">
      {/* 필터 */}
      {isFilterOpen ? (
        <Filter
          activeFilterItem={activeFilterItem}
          closeFilter={() => {
            setIsFilterOpen(false);
          }}
          filters={data?.collection.products.filters || []}
          loading={loading}
          setActiveFilterItem={setActiveFilterItem}
          totalProductCount={getTotalProductCount(data?.collection.products.filters || [])}
        />
      ) : null}
      <section className="pc:col-start-4 pc:col-end-13 pc:mt-5 pc:gap-20 col-span-6 flex flex-col gap-10">
        <section className="pc:gap-10 flex flex-col gap-5" id="horizontal-product-scroll">
          <section className="flex flex-col gap-5">
            <PageTitle page="collection">TRENDING</PageTitle>
            <p>Updated - {familyRankingUpdatedAt}</p>
          </section>
          <ul className={classNames("pc:gap-x-5 flex flex-row gap-x-2.5 overflow-x-auto overflow-y-hidden")}>
            {familyRanking.map(({ node }, index) => {
              const product = node as Product;
              return (
                <li className="pc:w-1/5 w-2/5 shrink-0" key={product.id}>
                  <ProductCard
                    availableForSale={product.availableForSale}
                    compareAtPrice={
                      product.variants ? parseInt(product.variants.edges[0]?.node?.compareAtPrice?.amount ?? "0") : 0
                    }
                    featuredImage={product.featuredImage!}
                    handle={product.handle}
                    isSaleShutDown={isSaleShutDown}
                    launchData={getProductLaunch(product)}
                    price={product.variants ? parseInt(product.variants.edges[0]?.node.price?.amount ?? "0") : 0}
                    product={product}
                    rankedNumber={index + 1}
                    tags={product.tags}
                    title={product.title}
                    vendor={product.vendor}
                  />
                </li>
              );
            })}
          </ul>
        </section>
        <section className="pc:mt-5 pc:gap-10 flex flex-col gap-5">
          {/* 컬렉션 title, description, sort */}
          <section className="flex flex-col gap-5">
            <div className="flex justify-between">
              <PageTitle page="collection">{data?.collection.title.replace("(Archive Sale)", "")}</PageTitle>
              <div className="flex gap-1">
                <label className="uppercase" htmlFor="sort">
                  Sort:
                </label>
                <Select
                  id="sort"
                  onChange={(event) => {
                    const index = sortList.findIndex((item) => item.value === event.target.value);
                    const query = qs.parse(location.search.replace("?", ""));
                    setSearchParams({
                      ...query,
                      sortIndex: index.toString(),
                    });
                  }}
                  options={sortList}
                  selected={{ value: sortList[sortIndex]?.value, name: sortList[sortIndex]?.name }}
                  styleType="text"
                />
              </div>
            </div>
          </section>
          {/* 컬렉션 상품 카드 목록 */}
          <section className="relative">
            <div
              className={classNames(
                "absolute top-0 right-0 bottom-0 left-0 bg-white transition-opacity duration-200",
                loading ? "z-20 opacity-70" : "-z-10 opacity-0",
              )}
            />
            <ProductGrid isSaleShutDown={isSaleShutDown} page="collection" products={products} />
          </section>

          {/* scroll 하단 상품 호출 */}
          <div className="w-full" id="loader" ref={loaderEl} />

          {/* 컬렉션 상품 조회 loading */}
          <div
            className={classNames(
              !hasNextPage && "pc:hidden",
              "pc:static pc:m-0 pc:flex pc:h-48 pc:w-full sticky bottom-5 z-20 mx-auto mt-5 flex items-center justify-center",
            )}
          >
            {isPC ? (
              loading && <LoadingIcon type="spinner" />
            ) : (
              <Button
                className="min-w-[82.2px] justify-center"
                onClick={() => {
                  setIsFilterOpen(true);
                }}
                styleType="secondary"
              >
                {loading ? <LoadingIcon type="spinner" /> : "Filter"}
              </Button>
            )}
          </div>

          {/* 마지막 페이지 */}
          {products.length > 0 && !hasNextPage && (
            <Button
              className="pc:my-5 mx-auto mt-5 uppercase underline"
              onClick={() => {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              variant="text"
            >
              Back To Top
            </Button>
          )}
        </section>
      </section>
    </section>
  );
}
